"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { getOrderConfig } from "@/lib/order";
import {
  bumpCartMeta,
  claimGuestCartToCloud,
  clearCloudCart,
  clearGuestCartStorage,
  clearLegacyCartStorage,
  createEmptyCartState,
  extractCartEnvelope,
  getCloudCart,
  getSelectedAddonsMap,
  mergeAddonToggle,
  normalizeCartState,
  normalizeCartTemplate,
  readGuestCartFromStorage,
  readLegacyCartAsGuestState,
  readUserCartCache,
  setActivePeriod,
  toInvitationItem,
  upsertCloudCart,
  writeGuestCartToStorage,
  writeUserCartCache,
} from "@/lib/cart";

const CartContext = createContext(null);
const CLOUD_SYNC_DEBOUNCE_MS = 600;
const CLOUD_SYNC_MAX_BACKOFF_MS = 30000;

function buildSafeContactDraft(contact) {
  return {
    email: String(contact?.email || "").trim(),
    phone: String(contact?.phone || "").trim(),
  };
}

function resetTemplateScopedDraft(existingDraft) {
  const emptyDraft = createEmptyCartState("GUEST").cart;
  return {
    ...emptyDraft,
    contact: buildSafeContactDraft(existingDraft?.contact || {}),
  };
}

function hasTemplate(state) {
  return Boolean(state?.template?.id || state?.template?.slug);
}

function ensureOwnerType(state, ownerType) {
  const normalized = normalizeCartState(state, ownerType);
  return {
    ...normalized,
    meta: {
      ...normalized.meta,
      owner_type: ownerType,
    },
  };
}

function toTimestampMs(value) {
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed : NaN;
}

function shouldPreferLocalCart(localState, remoteState) {
  const localHasTemplate = hasTemplate(localState);
  const remoteHasTemplate = hasTemplate(remoteState);

  if (localHasTemplate && !remoteHasTemplate) {
    return true;
  }
  if (!localHasTemplate && remoteHasTemplate) {
    return false;
  }

  const localUpdatedAtMs = toTimestampMs(localState?.meta?.updated_at);
  const remoteUpdatedAtMs = toTimestampMs(remoteState?.meta?.updated_at);

  if (Number.isFinite(localUpdatedAtMs) && Number.isFinite(remoteUpdatedAtMs)) {
    return localUpdatedAtMs >= remoteUpdatedAtMs;
  }
  if (Number.isFinite(localUpdatedAtMs) && !Number.isFinite(remoteUpdatedAtMs)) {
    return true;
  }
  if (!Number.isFinite(localUpdatedAtMs) && Number.isFinite(remoteUpdatedAtMs)) {
    return false;
  }

  return true;
}

function extractCartConflictPayload(error) {
  const root = error?.payload?.data || error?.payload || {};
  const conflict = root?.conflict || {};

  const serverCart =
    conflict?.server_cart && typeof conflict.server_cart === "object"
      ? conflict.server_cart
      : conflict?.serverCart && typeof conflict.serverCart === "object"
        ? conflict.serverCart
        : null;

  const serverRevisionRaw =
    conflict?.server_revision ?? conflict?.serverRevision ?? null;
  const parsedServerRevision = Number(serverRevisionRaw);
  const serverRevision = Number.isFinite(parsedServerRevision)
    ? parsedServerRevision
    : null;

  const serverUpdatedAt =
    conflict?.server_updated_at || conflict?.serverUpdatedAt || null;

  const policyHint = String(
    root?.conflict_policy_hint || root?.conflictPolicyHint || "",
  )
    .trim()
    .toUpperCase();

  return {
    serverCart,
    serverRevision,
    serverUpdatedAt,
    policyHint,
  };
}

function isJsonDeepEqual(firstValue, secondValue) {
  if (firstValue === secondValue) {
    return true;
  }

  try {
    return JSON.stringify(firstValue) === JSON.stringify(secondValue);
  } catch {
    return false;
  }
}

export function CartProvider({ children }) {
  const { authReady, isAuthenticated, user } = useAuth();

  const [cartState, setCartState] = useState(() => createEmptyCartState("GUEST"));
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [isCartSyncing, setIsCartSyncing] = useState(false);

  const [orderConfig, setOrderConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  const debouncedSyncTimerRef = useRef(null);
  const retrySyncTimerRef = useRef(null);
  const retryPendingStateRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const queuedCloudStateRef = useRef(null);
  const cloudSyncInFlightRef = useRef(false);
  const performCloudSyncRef = useRef(async () => {});

  const clearSyncTimers = useCallback(() => {
    if (debouncedSyncTimerRef.current) {
      clearTimeout(debouncedSyncTimerRef.current);
      debouncedSyncTimerRef.current = null;
    }
    if (retrySyncTimerRef.current) {
      clearTimeout(retrySyncTimerRef.current);
      retrySyncTimerRef.current = null;
    }
    retryPendingStateRef.current = null;
  }, []);

  const fetchConfig = useCallback(() => {
    Promise.resolve().then(async () => {
      setIsConfigLoading(true);
      try {
        const res = await getOrderConfig();
        if (res?.data) {
          setOrderConfig(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch order config", err);
      } finally {
        setIsConfigLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (open) {
      fetchConfig();
    }
  }, [open, fetchConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const persistedGuest = readGuestCartFromStorage();
      const hasPersistedGuest = hasTemplate(persistedGuest);
      const legacyGuest = hasPersistedGuest ? null : readLegacyCartAsGuestState();
      const nextGuestState = hasTemplate(legacyGuest) ? legacyGuest : persistedGuest;

      if (hasTemplate(nextGuestState)) {
        setCartState(ensureOwnerType(nextGuestState, "GUEST"));
        writeGuestCartToStorage(nextGuestState);
      } else {
        setCartState(createEmptyCartState("GUEST"));
      }

      if (hasTemplate(legacyGuest)) {
        clearLegacyCartStorage();
      }

      setHydrated(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  const performCloudSync = useCallback(async (stateToSync) => {
    if (!authReady || !isAuthenticated) {
      return;
    }

    const normalizedForCloud = ensureOwnerType(stateToSync, "USER");
    if (cloudSyncInFlightRef.current) {
      queuedCloudStateRef.current = normalizedForCloud;
      return;
    }

    cloudSyncInFlightRef.current = true;
    let syncFailed = false;
    try {
      const response = await upsertCloudCart(normalizedForCloud);
      const remoteCart = extractCartEnvelope(response);
      if (remoteCart) {
        const normalizedRemote = ensureOwnerType(remoteCart, "USER");
        setCartState(normalizedRemote);
        writeUserCartCache(normalizedRemote);

        if (queuedCloudStateRef.current) {
          queuedCloudStateRef.current = {
            ...queuedCloudStateRef.current,
            meta: {
              ...queuedCloudStateRef.current.meta,
              revision: Number(normalizedRemote.meta?.revision || 0),
            },
          };
        }
      }

      retryAttemptRef.current = 0;
      retryPendingStateRef.current = null;
      if (retrySyncTimerRef.current) {
        clearTimeout(retrySyncTimerRef.current);
        retrySyncTimerRef.current = null;
      }
    } catch (error) {
      const isRevisionConflict = Number(error?.status || 0) === 409;
      if (isRevisionConflict) {
        const conflictPayload = extractCartConflictPayload(error);
        const conflictServerCart = conflictPayload.serverCart
          ? ensureOwnerType(conflictPayload.serverCart, "USER")
          : null;
        let hasRetriedLocalState = false;

        const applyRecoveredState = async (remoteState) => {
          const normalizedRemote = ensureOwnerType(remoteState, "USER");
          const policyHint = conflictPayload.policyHint;
          const shouldAcceptServerSnapshot =
            policyHint === "SERVER_WINS"
            || (policyHint === "LATEST_WINS" && hasRetriedLocalState);
          const shouldRetryWithLocal =
            !shouldAcceptServerSnapshot
            && (
              policyHint === "LATEST_WINS"
              || shouldPreferLocalCart(normalizedForCloud, normalizedRemote)
            );

          if (shouldRetryWithLocal) {
            hasRetriedLocalState = true;
            const rebasedLocalState = {
              ...normalizedForCloud,
              meta: {
                ...normalizedForCloud.meta,
                owner_type: "USER",
                revision: Number(
                  conflictPayload.serverRevision
                    ?? normalizedRemote.meta?.revision
                    ?? 0,
                ),
                updated_at:
                  normalizedForCloud.meta?.updated_at
                  || conflictPayload.serverUpdatedAt
                  || normalizedRemote.meta?.updated_at,
              },
            };

            try {
              const retryResponse = await upsertCloudCart(rebasedLocalState);
              const retryRemoteCart = extractCartEnvelope(retryResponse);
              const normalizedRecovered = ensureOwnerType(
                retryRemoteCart || rebasedLocalState,
                "USER",
              );
              setCartState(normalizedRecovered);
              writeUserCartCache(normalizedRecovered);
            } catch {
              setCartState(normalizedRemote);
              writeUserCartCache(normalizedRemote);
            }
          } else {
            setCartState(normalizedRemote);
            writeUserCartCache(normalizedRemote);
          }

          retryAttemptRef.current = 0;
          retryPendingStateRef.current = null;
          if (retrySyncTimerRef.current) {
            clearTimeout(retrySyncTimerRef.current);
            retrySyncTimerRef.current = null;
          }
        };

        if (conflictServerCart) {
          try {
            await applyRecoveredState(conflictServerCart);
            return;
          } catch {
            // Fallback to GET /cart below if recovery from conflict payload fails.
          }
        }

        try {
          const remoteResponse = await getCloudCart();
          const remoteCart = extractCartEnvelope(remoteResponse);
          if (remoteCart) {
            await applyRecoveredState(remoteCart);
            return;
          }
        } catch {
          // Fallback to exponential retry below when conflict recovery fails.
        }
      }

      syncFailed = true;
      const attempt = retryAttemptRef.current;
      const backoffDelay = Math.min(
        CLOUD_SYNC_MAX_BACKOFF_MS,
        1000 * (2 ** attempt),
      );
      retryAttemptRef.current = attempt + 1;
      const retryState = queuedCloudStateRef.current || normalizedForCloud;
      retryPendingStateRef.current = retryState;

      if (retrySyncTimerRef.current) {
        clearTimeout(retrySyncTimerRef.current);
      }

      retrySyncTimerRef.current = setTimeout(() => {
        retryPendingStateRef.current = null;
        void performCloudSyncRef.current(retryState);
      }, backoffDelay);
    } finally {
      cloudSyncInFlightRef.current = false;

      if (syncFailed) {
        return;
      }

      const queued = queuedCloudStateRef.current;
      if (queued) {
        queuedCloudStateRef.current = null;
        void performCloudSyncRef.current(queued);
      }
    }
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    performCloudSyncRef.current = performCloudSync;
  }, [performCloudSync]);

  const scheduleCloudSync = useCallback((nextState, mode = "immediate") => {
    if (!authReady || !isAuthenticated) {
      return;
    }

    const normalizedState = ensureOwnerType(nextState, "USER");

    if (mode === "debounced") {
      queuedCloudStateRef.current = normalizedState;
      if (debouncedSyncTimerRef.current) {
        clearTimeout(debouncedSyncTimerRef.current);
      }

      debouncedSyncTimerRef.current = setTimeout(() => {
        const pending = queuedCloudStateRef.current;
        if (!pending) {
          return;
        }
        queuedCloudStateRef.current = null;
        void performCloudSync(pending);
      }, CLOUD_SYNC_DEBOUNCE_MS);
      return;
    }

    if (debouncedSyncTimerRef.current) {
      clearTimeout(debouncedSyncTimerRef.current);
      debouncedSyncTimerRef.current = null;
    }
    queuedCloudStateRef.current = null;
    void performCloudSync(normalizedState);
  }, [authReady, isAuthenticated, performCloudSync]);

  const flushCloudSync = useCallback(async () => {
    if (!authReady || !isAuthenticated) {
      return true;
    }

    if (debouncedSyncTimerRef.current) {
      clearTimeout(debouncedSyncTimerRef.current);
      debouncedSyncTimerRef.current = null;
    }

    if (retrySyncTimerRef.current) {
      clearTimeout(retrySyncTimerRef.current);
      retrySyncTimerRef.current = null;
    }

    const retryPendingState = retryPendingStateRef.current;
    retryPendingStateRef.current = null;
    if (retryPendingState) {
      if (cloudSyncInFlightRef.current) {
        queuedCloudStateRef.current = retryPendingState;
      } else {
        await performCloudSync(retryPendingState);
      }
    }

    const queuedState = queuedCloudStateRef.current;
    if (queuedState && !cloudSyncInFlightRef.current) {
      queuedCloudStateRef.current = null;
      await performCloudSync(queuedState);
    }

    const timeoutMs = 8000;
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (
        !cloudSyncInFlightRef.current &&
        !queuedCloudStateRef.current &&
        !retrySyncTimerRef.current
      ) {
        return true;
      }

      if (!cloudSyncInFlightRef.current) {
        if (retrySyncTimerRef.current) {
          clearTimeout(retrySyncTimerRef.current);
          retrySyncTimerRef.current = null;
        }

        const nextState =
          retryPendingStateRef.current || queuedCloudStateRef.current;
        if (nextState) {
          retryPendingStateRef.current = null;
          queuedCloudStateRef.current = null;
          await performCloudSync(nextState);
          continue;
        }
      }

      await new Promise((resolve) => {
        setTimeout(resolve, 50);
      });
    }

    return false;
  }, [authReady, isAuthenticated, performCloudSync]);

  const mutateCartState = useCallback((updater, syncMode = "immediate") => {
    const ownerType = isAuthenticated ? "USER" : "GUEST";
    
    const baseState = ensureOwnerType(cartState, ownerType);
    const updatedState = updater(baseState);
    if (updatedState === baseState) {
      return;
    }

    let nextState;
    if (isAuthenticated) {
      const normalizedUpdatedState = ensureOwnerType(updatedState, ownerType);
      nextState = {
        ...normalizedUpdatedState,
        meta: {
          ...normalizedUpdatedState.meta,
          owner_type: ownerType,
          updated_at: new Date().toISOString(),
          revision: Number(baseState?.meta?.revision || 0),
        },
      };
    } else {
      nextState = bumpCartMeta(updatedState, ownerType);
    }

    setCartState(nextState);

    if (isAuthenticated) {
      writeUserCartCache(nextState);
      scheduleCloudSync(nextState, syncMode);
    } else {
      writeGuestCartToStorage(nextState);
    }
  }, [cartState, isAuthenticated, scheduleCloudSync]);

  const loadCloudCartState = useCallback(async () => {
    if (!authReady || !isAuthenticated) {
      return;
    }

    setIsCartSyncing(true);
    try {
      const guestState = readGuestCartFromStorage();
      const hasGuestState = hasTemplate(guestState);

      if (hasGuestState) {
        try {
          const claimResponse = await claimGuestCartToCloud(guestState, "LATEST_WINS");
          const claimedCart = extractCartEnvelope(claimResponse);
          if (claimedCart) {
            const normalizedClaimed = ensureOwnerType(claimedCart, "USER");
            setCartState(normalizedClaimed);
            writeUserCartCache(normalizedClaimed);
            clearGuestCartStorage();
            clearLegacyCartStorage();
            return;
          }
        } catch {
          // fallback to GET /cart
        }
      }

      const response = await getCloudCart();
      const cloudCart = extractCartEnvelope(response);
      if (cloudCart) {
        const normalizedCloud = ensureOwnerType(cloudCart, "USER");
        setCartState(normalizedCloud);
        writeUserCartCache(normalizedCloud);
      } else {
        setCartState((previous) => ensureOwnerType(previous, "USER"));
      }

      clearGuestCartStorage();
      clearLegacyCartStorage();
    } catch {
      const cachedUserCart = readUserCartCache();
      if (hasTemplate(cachedUserCart)) {
        setCartState(ensureOwnerType(cachedUserCart, "USER"));
      } else {
        setCartState((previous) => ensureOwnerType(previous, "USER"));
      }
    } finally {
      setIsCartSyncing(false);
    }
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    if (!hydrated || !authReady) {
      return;
    }

    let cancelled = false;
    async function hydrateByAuth() {
      if (!isAuthenticated) {
        setIsCartSyncing(false);
        clearSyncTimers();
        const guestState = readGuestCartFromStorage();
        if (hasTemplate(guestState)) {
          if (!cancelled) {
            setCartState(ensureOwnerType(guestState, "GUEST"));
          }
          return;
        }

        if (!cancelled) {
          setCartState((previous) => {
            const nextGuest = ensureOwnerType(previous, "GUEST");
            writeGuestCartToStorage(nextGuest);
            return nextGuest;
          });
        }
        return;
      }

      const cachedUserCart = readUserCartCache();
      if (hasTemplate(cachedUserCart) && !cancelled) {
        setCartState(ensureOwnerType(cachedUserCart, "USER"));
      }

      if (!cancelled) {
        await loadCloudCartState();
      }
    }

    void hydrateByAuth();
    return () => {
      cancelled = true;
    };
  }, [
    hydrated,
    authReady,
    isAuthenticated,
    loadCloudCartState,
    clearSyncTimers,
  ]);

  useEffect(() => () => {
    clearSyncTimers();
  }, [clearSyncTimers]);

  const items = useMemo(
    () => toInvitationItem(cartState.template),
    [cartState.template],
  );

  const selectedAddons = getSelectedAddonsMap(cartState?.cart?.order);

  const masaAktif = String(
    cartState?.cart?.order?.activePeriod ||
      cartState?.cart?.order?.addons?.find((addon) => addon?.key === "activePeriod")?.value ||
      "6",
  );

  const customDomainValue = String(cartState?.cart?.order?.customDomain || "");
  const selectedAddonsCount = Object.values(selectedAddons).filter(Boolean).length;
  const totalItems = items.length + selectedAddonsCount;
  const subtotal = items.reduce((sum, item) => sum + Number(item.price || 0), 0);

  const applyTemplateToCart = useCallback((templateLike, showDrawer = true) => {
    const normalizedTemplate = normalizeCartTemplate(templateLike);
    if (!normalizedTemplate) {
      return;
    }

    mutateCartState((previous) => {
      const currentTemplateId = previous?.template?.id || previous?.template?.slug;
      const nextTemplateId = normalizedTemplate.id || normalizedTemplate.slug;
      if (currentTemplateId && nextTemplateId && currentTemplateId === nextTemplateId) {
        return previous;
      }

      return {
        ...previous,
        template: normalizedTemplate,
        cart: resetTemplateScopedDraft(previous.cart),
      };
    }, "immediate");

    if (showDrawer) {
      setOpen(true);
    }
  }, [mutateCartState]);

  const addItem = useCallback((product) => {
    applyTemplateToCart(product, true);
  }, [applyTemplateToCart]);

  const replaceItem = useCallback((product) => {
    applyTemplateToCart(product, true);
  }, [applyTemplateToCart]);

  const clearCart = useCallback(() => {
    const ownerType = isAuthenticated ? "USER" : "GUEST";
    const emptyState = createEmptyCartState(ownerType);
    setCartState(emptyState);
    clearLegacyCartStorage();

    if (isAuthenticated) {
      writeUserCartCache(emptyState);
      clearGuestCartStorage();
      clearSyncTimers();
      void clearCloudCart().catch(() => {});
    } else {
      writeGuestCartToStorage(emptyState);
    }
  }, [isAuthenticated, clearSyncTimers]);

  const removeItem = useCallback((id) => {
    const currentId = cartState?.template?.id || cartState?.template?.slug;
    if (id !== currentId) {
      return;
    }
    clearCart();
  }, [cartState.template, clearCart]);

  const updateQty = useCallback((id, qty) => {
    if (qty < 1) {
      removeItem(id);
    }
  }, [removeItem]);

  const toggleAddon = useCallback((key) => {
    const cleanKey = String(key || "").trim();
    if (!cleanKey) {
      return;
    }

    const isCurrentlySelected = Boolean(selectedAddons[cleanKey]);
    mutateCartState((previous) => {
      const newOrder = mergeAddonToggle(previous.cart.order, cleanKey, !isCurrentlySelected);
      // We assume mergeAddonToggle might return the same reference or we can check equality here if needed,
      // but toggleAddon inherently changes the state by flipping a boolean, so it always changes state.
      // However, we can just let it run.
      return {
        ...previous,
        cart: {
          ...previous.cart,
          order: newOrder,
        },
      };
    }, "immediate");
  }, [mutateCartState, selectedAddons]);

  const setMasaAktif = useCallback((value) => {
    mutateCartState((previous) => {
      const newOrder = setActivePeriod(previous.cart.order, value);
      // setActivePeriod is likely changing value. If value is same, we could skip it.
      // But it's usually called on user interaction (select option).
      return {
        ...previous,
        cart: {
          ...previous.cart,
          order: newOrder,
        },
      };
    }, "immediate");
  }, [mutateCartState]);

  const setCustomDomainValue = useCallback((value) => {
    mutateCartState((previous) => {
      const currentVal = previous.cart.order?.customDomain || "";
      const newVal = String(value || "");
      if (currentVal === newVal) {
        return previous;
      }
      return {
        ...previous,
        cart: {
          ...previous.cart,
          order: {
            ...previous.cart.order,
            customDomain: newVal,
          },
        },
      };
    }, "debounced");
  }, [mutateCartState]);

  const setCheckoutContact = useCallback((patch) => {
    mutateCartState((previous) => {
      const currentContact = previous.cart.contact || {};
      const newContact = buildSafeContactDraft({
        ...currentContact,
        ...patch,
      });

      if (
        currentContact.email === newContact.email &&
        currentContact.phone === newContact.phone
      ) {
        return previous;
      }

      return {
        ...previous,
        cart: {
          ...previous.cart,
          contact: newContact,
        },
      };
    }, "debounced");
  }, [mutateCartState]);

  const setCheckoutCoupleDetails = useCallback((patch) => {
    mutateCartState((previous) => {
      const current = previous.cart.coupleDetails || {};
      const bride = String(patch?.bride ?? current.bride ?? "");
      const groom = String(patch?.groom ?? current.groom ?? "");
      const weddingDate = String(patch?.weddingDate ?? current.weddingDate ?? "");
      const notes = String(patch?.notes ?? current.notes ?? "");

      if (
        current.bride === bride &&
        current.groom === groom &&
        current.weddingDate === weddingDate &&
        current.notes === notes
      ) {
        return previous;
      }

      return {
        ...previous,
        cart: {
          ...previous.cart,
          coupleDetails: { bride, groom, weddingDate, notes },
        },
      };
    }, "debounced");
  }, [mutateCartState]);

  const setRequestedPublicSlug = useCallback((requestedPublicSlug) => {
    mutateCartState((previous) => {
      const currentSlug = previous.cart.order?.requestedPublicSlug || "";
      const newSlug = String(requestedPublicSlug || "").trim().toLowerCase();

      if (currentSlug === newSlug) {
        return previous;
      }

      return {
        ...previous,
        cart: {
          ...previous.cart,
          order: {
            ...previous.cart.order,
            requestedPublicSlug: newSlug,
          },
        },
      };
    }, "debounced");
  }, [mutateCartState]);

  const setCloudDiyDraft = useCallback((cloudDiyPatch) => {
    mutateCartState((previous) => {
      const previousCloudDiy = previous?.cart?.cloudDiy || {};
      const normalizedPatch = {
        ready:
          cloudDiyPatch?.ready === undefined
            ? previousCloudDiy?.ready
            : Boolean(cloudDiyPatch.ready),
        payload:
          cloudDiyPatch?.payload && typeof cloudDiyPatch.payload === "object"
            ? cloudDiyPatch.payload
            : previousCloudDiy?.payload || {},
      };
      const nextCloudDiy = {
        ...previousCloudDiy,
        ...normalizedPatch,
      };

      const isSameCloudDiy =
        Boolean(previousCloudDiy?.ready) === Boolean(nextCloudDiy?.ready) &&
        isJsonDeepEqual(previousCloudDiy?.payload || {}, nextCloudDiy?.payload || {});

      if (isSameCloudDiy) {
        return previous;
      }

      return {
        ...previous,
        cart: {
          ...previous.cart,
          cloudDiy: nextCloudDiy,
        },
      };
    }, "immediate");
  }, [mutateCartState]);

  const contextValue = useMemo(() => ({
    items,
    open,
    setOpen,
    addItem,
    replaceItem,
    removeItem,
    updateQty,
    clearCart,
    totalItems,
    subtotal,
    selectedAddons,
    toggleAddon,
    selectedAddonsCount,
    masaAktif,
    setMasaAktif,
    customDomainValue,
    setCustomDomainValue,
    hydrated,
    orderConfig,
    isConfigLoading,
    cartState,
    isCartSyncing,
    refreshCloudCart: loadCloudCartState,
    flushCloudSync,
    setCheckoutContact,
    setCheckoutCoupleDetails,
    setRequestedPublicSlug,
    setCloudDiyDraft,
    cartOwnerType: isAuthenticated ? "USER" : "GUEST",
    cartOwnerId: user?.id || null,
  }), [
    items,
    open,
    addItem,
    replaceItem,
    removeItem,
    updateQty,
    clearCart,
    totalItems,
    subtotal,
    selectedAddons,
    toggleAddon,
    selectedAddonsCount,
    masaAktif,
    setMasaAktif,
    customDomainValue,
    setCustomDomainValue,
    hydrated,
    orderConfig,
    isConfigLoading,
    cartState,
    isCartSyncing,
    loadCloudCartState,
    flushCloudSync,
    setCheckoutContact,
    setCheckoutCoupleDetails,
    setRequestedPublicSlug,
    setCloudDiyDraft,
    isAuthenticated,
    user?.id,
  ]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return ctx;
}
